"use client";

import type { TechnologyStats } from "@/util/contributions";
import { Badge } from "./ui/badge";
import { FilterChip } from "./ui/filter-chip";

/**
 * Visual breakdown of technologies as a horizontal bar chart
 */
export function TechnologyBreakdown({
  stats,
  activeTech,
  onTechClick,
}: {
  stats: TechnologyStats[];
  activeTech?: string | null;
  onTechClick?: (tech: string | null) => void;
}) {
  // Show top 9 technologies (matching the 9 contribution types for visual balance)
  const topStats = stats.slice(0, 9);

  if (topStats.length === 0) {
    return null;
  }

  const handlePressedChange = (tech: string, pressed: boolean) => {
    if (!onTechClick) return;
    // Toggle on/off based on pressed state
    onTechClick(pressed ? tech : null);
  };

  return (
    <section data-testid="technology-breakdown" className="space-y-4">
      <div className="space-y-1.5">
        <div className="text-lg/9 font-semibold tracking-tight">
          Top Technologies
        </div>
      </div>
      <div className="grid min-w-0 grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {topStats.map((stat) => {
          const isActive = activeTech === stat.technology;
          const isClickable = Boolean(onTechClick);

          // Stable key - no remount hack needed with controlled state
          return (
            <FilterChip
              key={stat.technology}
              pressed={isActive}
              onPressedChange={(pressed) =>
                handlePressedChange(stat.technology, pressed)
              }
              isClickable={isClickable}
              data-testid="technology-chip"
            >
              <div className="shrink-0">
                <Badge
                  data-testid="technology-badge"
                  variant="outline"
                  className="group-data-[state=on]:bg-accent group-data-[state=on]:text-accent-foreground [@media(hover:hover)_and_(pointer:fine)]:group-hover:bg-muted dark:border-border border-[rgba(0,0,0,0.08)] bg-[rgba(0,0,0,0.05)] text-base font-medium transition-colors dark:bg-transparent"
                >
                  {stat.technology}
                </Badge>
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <div className="dark:bg-muted dark:group-data-[state=on]:bg-muted h-2 flex-1 overflow-hidden rounded-full bg-[rgba(0,0,0,0.08)] group-data-[state=on]:bg-[rgba(0,0,0,0.08)]">
                  <div
                    className="bg-brand/50 group-data-[state=on]:bg-brand h-full rounded-full transition-all duration-300 motion-reduce:transition-none"
                    style={{ width: `${stat.percentage}%` }}
                    role="progressbar"
                    aria-valuenow={stat.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${stat.technology}: ${stat.percentage.toFixed(0)}%`}
                  />
                </div>
                <span className="text-muted-foreground w-20 shrink-0 text-right text-base tabular-nums">
                  {stat.repoCount} ({stat.percentage.toFixed(0)}%)
                </span>
              </div>
            </FilterChip>
          );
        })}
      </div>
    </section>
  );
}
